import React, { useEffect, useState } from 'react';
import { Container, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useToken from '../../hooks/useToken';
import ProgressTracker from '../shared/ProgressTracker';
import useProgressRedirect from '../../hooks/useProgressRedirect';

function Consent() {
  const { token, refreshAccessToken } = useToken();
  const [doc, setDoc] = useState(null); // { title, version, pdf_url }
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [alreadyConsented, setAlreadyConsented] = useState(false);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [viewing, setViewing] = useState(false);
  const navigate = useNavigate();

  // Only allow access when the player's current progress is at CONSENT
  useProgressRedirect('CONSENT');

  useEffect(() => {
    const fetchConsent = async () => {
      try {
        const [docRes, statusRes] = await Promise.all([
          fetch('/api' + '/consent', { headers: { Authorization: `Bearer ${token}` } }),
          fetch('/api' + '/consent/status', { headers: { Authorization: `Bearer ${token}` } })
        ]);

        if (!docRes.ok) throw new Error('Failed to load consent document.');
        const docJson = await docRes.json();
        setDoc(docJson);

        if (statusRes.ok) {
          const statusJson = await statusRes.json();
          setAlreadyConsented(Boolean(statusJson?.consented));
        }
      } catch (err) {
        setError(err.message || 'Failed to load consent info.');
      } finally {
        setLoading(false);
      }
    };
    fetchConsent();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!alreadyConsented && !checked) {
      setError('Please confirm that you have read and agree to participate.');
      return;
    }

    setSubmitting(true);
    try {
      const authToken = token || (await refreshAccessToken());

      // Store consent (idempotent on backend)
      const agreeRes = await fetch('/api' + '/consent/agree', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ version: doc?.version }),
      });

      if (!agreeRes.ok) {
        const err = await agreeRes.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to record consent.');
      }

      // Advance progress to PRE_EXPERIMENT
      const progressRes = await fetch('/api' + '/player/progress', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ progress_step: 'PRE_EXPERIMENT' }),
      });

      if (progressRes.ok) {
        navigate('/pre-experiment', { replace: true });
        return;
      } else {
        const err = await progressRes.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to update progress.');
      }
    } catch (err) {
      setError(err.message || 'Unexpected error submitting consent.');
    }
    setSubmitting(false);
  };

  return (
    <Container className="py-4">
      <ProgressTracker currentStep="consent" />
      {(loading || submitting) && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : (<>

      <h2 className="mb-4">📄 Informed Consent</h2>
      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit}>
        <div className="mb-3">
          <p className="mb-2">
            Please read the Informed Consent document and download it for your
            records. After reading, tick the checkbox to indicate consent.
          </p>
          <Button
            variant="outline-primary"
            className="me-2"
            onClick={async (e) => {
              e.preventDefault();
              setError('');
              try {
                const authToken = token || (await refreshAccessToken());
                const res = await fetch('/api/consent/download', {
                  method: 'GET',
                  headers: {
                    Authorization: `Bearer ${authToken}`,
                  },
                });

                if (!res.ok) {
                  const err = await res.json().catch(() => ({}));
                  throw new Error(err.message || 'Failed to download consent PDF.');
                }

                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                // try to derive filename from response headers, fallback to consent.pdf
                const contentDisposition = res.headers.get('content-disposition') || '';
                let filename = 'consent.pdf';
                const match = /filename="?([^";]+)"?/.exec(contentDisposition);
                if (match && match[1]) filename = match[1];
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              } catch (err) {
                setError(err.message || 'Failed to download consent.');
              }
            }}
          >
            Download PDF
          </Button>
          <Button
            variant="outline-secondary"
            className="me-2"
            onClick={async (e) => {
              e.preventDefault();
              setError('');
              try {
                const authToken = token || (await refreshAccessToken());
                const res = await fetch('/api/consent/download', {
                  method: 'GET',
                  headers: { Authorization: `Bearer ${authToken}` },
                });

                if (!res.ok) {
                  const err = await res.json().catch(() => ({}));
                  throw new Error(err.message || 'Failed to load consent PDF for viewing.');
                }

                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                setViewerUrl(url);
                setViewing(true);
              } catch (err) {
                setError(err.message || 'Failed to open viewer.');
              }
            }}
          >
            View PDF
          </Button>
          <span className="text-muted ms-2">Version: <strong>{doc?.version || 'N/A'}</strong></span>
        </div>

        {viewing && viewerUrl && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Consent document viewer</strong>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => {
                  setViewing(false);
                  if (viewerUrl) {
                    window.URL.revokeObjectURL(viewerUrl);
                    setViewerUrl(null);
                  }
                }}
              >
                Close Viewer
              </Button>
            </div>
            <iframe
              title="Consent Document"
              src={viewerUrl}
              style={{ width: '100%', height: '600px', border: '1px solid #ddd' }}
            />
          </div>
        )}

        <Form.Group className="mb-4">
          <Form.Check
            id="consent-check"
            type="checkbox"
            checked={alreadyConsented || checked}
            onChange={(e) => setChecked(e.target.checked)}
            label={
              "I have read and understood the information in this document and I have received an answer to all my questions regarding this research. I give my consent to participate."
            }
            disabled={alreadyConsented}
          />
          {alreadyConsented && (
            <Alert variant="success" className="mt-3">
              Consent already recorded. Click Continue to proceed.
            </Alert>
          )}
        </Form.Group>

        <Button
          type="submit"
          variant="primary"
          disabled={submitting || (!alreadyConsented && !checked)}
        >
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" /> Saving...
            </>
          ) : (
            'Start'
          )}
        </Button>
      </Form>
      </>)}
    </Container>
  );
}

export default Consent;
